/**
 * Vessel Systems & Controls - WBC3 Appendix 8 Section 7.2 Compliance
 * 
 * Core systems familiarisation for general crew
 * Implements conditional logic based on vessel configuration and crew setup
 * 
 * STRUCTURE:
 * - Core items applicable to most vessels
 * - Conditional items based on equipment presence and automation
 * - Engineer presence affects visibility on complex systems
 */

import logger from 'utils/logger';

export const VESSEL_SYSTEMS_CONTROLS_ITEMS = [
  {
    id: 'steering_controls',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Primary Steering Controls',
    description: 'Location and function of helm/steering system',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Ensure crew know where and how to control vessel steering.'
  },
  {
    id: 'emergency_steering',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency Steering Systems',
    description: 'Manual/emergency steering operations',
    trigger: (vessel) => vessel.emergency_steering === true,
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No emergency steering fitted', 'System not accessible to crew'],
    tooltip: 'Required if vessel is fitted with emergency/manual steering backup. CUSTOMIZE: Document your vessel\'s emergency steering location and procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'bilge_pump_controls',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Bilge Pump Controls & Indicators',
    description: 'Location and use of bilge pump systems',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'All crew should know bilge pump location and basic operation. Level of detail varies: engineer normally operates but crew need emergency awareness.'
  },
  {
    id: 'engine_control_panel',
    classification: 'VESSEL_SPECIFIC',
    role: 'ENGINEERING',
    text: 'Engine Control Panel Location & Operation',
    description: 'Basic familiarisation with engine starting/stopping interface',
    trigger: (vessel) => !vessel.engineer_onboard || parseFloat(vessel.length) < 15,
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Conditional',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['Dedicated engineer onboard with restricted access', 'Engine room access restricted'],
    tooltip: 'Show to all crew unless vessel has dedicated engineers AND restricted access. CUSTOMIZE: Add your vessel\'s engine control panel location and procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'fuel_shutoff',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Fuel Shutoff Valves',
    description: 'Location and operation of emergency fuel shutoff systems',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'All crew must know how to shut off fuel supply in an emergency.'
  },
  {
    id: 'emergency_engine_stops',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Emergency Engine Stop Systems',
    description: 'All methods to stop engines in emergency (kill cords, emergency stops, etc.)',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'All emergency engine stop methods including kill cords (RIBs), emergency stop buttons, and backup systems.'
  },
  {
    id: 'battery_isolation',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Battery Isolation Switches',
    description: 'Location and correct use of battery isolation',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Important for electrical fault response and emergency shutdown.'
  },
  {
    id: 'mooring_unmooring_procedures',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Mooring and Unmooring Procedures',
    description: 'Safe mooring and unmooring operations',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'All crew must understand safe mooring procedures including line handling and deck safety.'
  },
  {
    id: 'watertight_weathertight_closures',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Watertight/Weathertight Closures',
    description: 'Location and operation of all watertight/weathertight closures',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Location and operation of all WT/WT closures (doors, hatches, lockers). Ensure crew understand importance of keeping them secure.'
  },
  {
    id: 'basic_deck_machinery',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Basic Deck Machinery (Cleats, Bollards, Basic Winches)',
    description: 'Safe operation of standard deck fittings and mooring equipment',
    trigger: 'Always',
    reference: 'WBC3 Appendix 8 — General Familiarisation',
    tag: 'Required',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Basic deck equipment for mooring and general operations. All crew should understand safe operation.'
  }
];

/**
 * Generate Vessel Systems & Controls checklist
 * WBC3-compliant systems familiarisation per Appendix 8, Section 7.2 General Familiarisation
 * 
 * Implements vessel-specific conditional logic for systems training
 * based on equipment presence, automation level, and crew configuration
 */
export function generateVesselSystemsControlsChecklist(vessel, crew) {
  const items = [];
  
  // Debug logging
  logger.log('Vessel Systems Debug:', {
    vessel: vessel,
    emergency_steering: vessel?.emergency_steering,
    bilge_auto: vessel?.bilge_auto,
    engineer_onboard: vessel?.engineer_onboard,
    length: vessel?.length
  });
  
  VESSEL_SYSTEMS_CONTROLS_ITEMS.forEach(item => {
    // Check trigger condition
    let includeItem = false;
    
    if (item.trigger === 'Always') {
      includeItem = true;
    } else if (typeof item.trigger === 'function') {
      includeItem = item.trigger(vessel);
    }
    
    // Debug logging for conditional items
    if (typeof item.trigger === 'function') {
      logger.log(`Item ${item.id}: trigger result = ${includeItem}`, {
        trigger: item.trigger.toString(),
        vessel_props: {
          emergency_steering: vessel?.emergency_steering,
          bilge_auto: vessel?.bilge_auto,
          engineer_onboard: vessel?.engineer_onboard
        }
      });
    }
    
    if (includeItem) {
      // Add rank visibility based on system complexity
      const processedItem = {
        ...item,
        rankVisibility: {
          'Master': 'full_operational',
          'Engineer': 'full_operational',
          'Crew': item.id === 'engine_control_panel' && vessel.engineer_onboard ? 'general_awareness_supervised' : 'full_operational',
          'Crew Member': item.id === 'engine_control_panel' && vessel.engineer_onboard ? 'general_awareness_supervised' : 'full_operational',
          'Supernumerary': 'general_awareness_supervised'
        }
      };
      
      items.push(processedItem);
    }
  });
  
  return {
    section: 'vessel_systems_controls',
    title: 'Vessel Systems & Controls',
    items: items,
    completionRequired: true,
    auditTrail: true,
    description: 'Core systems familiarisation per WBC3 Appendix 8, Section 7.2 General Familiarisation'
  };
}

// Vessel Systems completion rules per WBC3 Appendix 8, Section 7.2
export const VESSEL_SYSTEMS_RULES = {
  // Required for crew operational competency
  required_for_operations: true,
  
  // Target completion time (systems overview)
  target_completion_minutes: 45,
  
  // Does not block initial operations but required for full familiarisation
  blocks_operations: false,
  
  // Applies to operational crew (not passengers/supernumeraries)
  applies_to_operational_crew: true,
  
  // Integration with other systems
  integration: {
    emergency_procedures: 'Essential for emergency response capability',
    equipment_checks: 'Supports equipment inspection competency',
    vessel_operations: 'Required for safe vessel operation'
  },
  
  // Audit requirements
  audit: {
    practical_demonstration_required: true,
    competency_assessment: true,
    master_sign_off_required: true,
    completion_timestamp: true
  }
};